/** AI assistant panel (§5.3–5.5, 5.7, 5.9, §5.10): every output lands in a review
 *  queue — accept, edit, or reject each item individually. Nothing reaches the takeoff,
 *  inventory, or a client document without a human accepting it. All features degrade
 *  to visible failure; the manual paths never depend on this panel. */
import { useState } from 'preact/hooks';
import { createRecord, createRecords, explainStorageError, listLive, putRecord, softDelete } from '../../core/db';
import { nowIso } from '../../core/ids';
import { fmtCents } from '../../core/money';
import type { InventoryItem, Job, PriceItem, ReviewItem, TakeoffLine } from '../../core/types';
import { aiMonthSig, explainAiError } from '../../ai/client';
import { scopeParse, bidMap, photoInventory, emailDraft, sanitySummary } from '../../ai/tasks';
import { bidMapCandidates, sanityCheck, parseSurvey, MIN_HISTORY } from '../../ai/rules';
import { resolveLines } from '../../docs/compute';
import { toast } from '../state';
import { Banner } from '../bits';

interface Props {
  job: Job;
  lines: TakeoffLine[];
  inventory: InventoryItem[];
  prices: PriceItem[];
  review: ReviewItem[];
  user: string;
  configured: boolean;
  onChanged: () => Promise<void>;
}

type LinePayload = { kind: 'line'; item: string; qty: number; label: string; confidence: string; basis?: string };
type InvPayload = { kind: 'inventory'; cat: InventoryItem['cat']; species?: string; qty: number; cond: InventoryItem['cond']; notes?: string; confidence: string };

export function AiPanel(p: Props) {
  const [scope, setScope] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<{ text: string; reason: string }[]>([]);
  const [emailKind, setEmailKind] = useState<'bid_follow_up' | 'schedule_change' | 'progress_summary'>('bid_follow_up');
  const [email, setEmail] = useState<{ subject: string; body: string } | null>(null);
  const [sanity, setSanity] = useState<{ flags: { kind: string; text: string }[]; enough: boolean; summary?: string } | null>(null);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [surveyText, setSurveyText] = useState('');

  const month = aiMonthSig.value;
  const blocked = month?.status === 'blocked';
  const aiOff = !p.configured || blocked;
  const offReason = !p.configured
    ? 'AI is not connected — add the proxy address and passphrase in Settings. Everything manual works without it.'
    : 'The monthly AI budget is used up; AI buttons are off until the 1st. Every manual path keeps working.';
  const pending = p.review.filter((r) => r.status === 'pending');

  function fail(e: unknown) { setErr(explainAiError(e)); }

  async function queue(feature: ReviewItem['feature'], payloads: (LinePayload | InvPayload)[], costUsd: number, model?: string) {
    for (const pay of payloads) {
      await createRecord('reviewQueue', {
        feature, jobId: p.job.id, payload: pay, model,
        confidence: (pay.confidence as ReviewItem['confidence']) ?? 'medium',
        costUsd, status: 'pending',
      }, { snapshot: false });
    }
    await p.onChanged();
  }

  // ---------- §5.5 natural-language scope ----------
  async function runScope() {
    if (!scope.trim()) { toast('Type the scope first'); return; }
    setBusy('scope'); setErr(null); setSkipped([]);
    try {
      const r = await scopeParse(scope, p.prices);
      await queue('scope_parse', r.data.lines.map((l) => ({ kind: 'line' as const, ...l })), r.cached ? 0 : r.costUsd);
      setSkipped(r.data.skipped);
      toast(`${r.data.lines.length} line(s) drafted for review${r.data.skipped.length ? `; ${r.data.skipped.length} skipped — listed below, never silently dropped` : ''}`);
      setScope('');
    } catch (e) { fail(e); } finally { setBusy(''); }
  }

  // ---------- §5.4 draft bid from site data (rules give candidates, model picks) ----------
  async function runBidMap() {
    if (p.inventory.length === 0) { setErr('Nothing in the site inventory yet. Run the scan or import a survey first — the draft bid maps inventory to priced lines.'); return; }
    setBusy('bidmap'); setErr(null);
    try {
      const r = await bidMap(p.inventory, p.job.parcel?.lotSqFt, p.prices, bidMapCandidates(p.inventory));
      await queue('bid_map', r.data.lines.map((l) => ({ kind: 'line' as const, ...l })), r.cached ? 0 : r.costUsd);
      toast(`${r.data.lines.length} proposed line(s) in the review queue — every one labeled with its basis`);
    } catch (e) { fail(e); } finally { setBusy(''); }
  }

  // ---------- §5.3 photos → inventory ----------
  async function runPhotos(ev: Event) {
    const files = Array.from((ev.target as HTMLInputElement).files ?? []).slice(0, 4);
    (ev.target as HTMLInputElement).value = '';
    if (!files.length) return;
    setBusy('photos'); setErr(null);
    try {
      const images: { mime: string; b64: string }[] = [];
      for (const f of files) {
        const small = await recompress(f, 1568);
        images.push(small);
        await createRecord('photos', {
          jobId: p.job.id, bytes: b64ToBuf(small.b64), mime: small.mime,
          capturedAt: nowIso(), caption: f.name,
        }, { snapshot: false });
      }
      const r = await photoInventory(images);
      await queue('photo_inventory', r.data.items.map((it) => ({ kind: 'inventory' as const, ...it })), r.cached ? 0 : r.costUsd);
      toast(`${r.data.items.length} inventory item(s) drafted from ${files.length} photo(s)${r.data.caveats ? ` — note: ${r.data.caveats}` : ''}`);
    } catch (e) { fail(e); } finally { setBusy(''); }
  }

  // ---------- §5.7 sanity check: rules always run; AI only phrases ----------
  async function runSanity() {
    setBusy('sanity'); setErr(null);
    const allDocs = await listLive('documents');
    const history = allDocs.filter((d) => d.jobId !== p.job.id);
    const resolved = resolveLines(p.lines, p.prices);
    const { flags, enoughHistory } = sanityCheck({ job: p.job, lines: p.lines, resolved, prices: p.prices, history });
    let summary: string | undefined;
    if (p.configured && !blocked && flags.length > 0) {
      try { summary = (await sanitySummary(flags.map((f) => f.text), p.job.title)).data.summary; }
      catch { /* the rules themselves are the check; the summary is garnish */ }
    }
    setSanity({ flags, enough: enoughHistory, summary });
    setBusy('');
  }

  // ---------- §5.9 email drafts — always a draft, the app never sends ----------
  async function runEmail() {
    setBusy('email'); setErr(null);
    try {
      const r = await emailDraft(emailKind, {
        jobTitle: p.job.title, siteAddress: p.job.siteAddress, bidNumber: p.job.bidNumber,
        status: p.job.status,
      });
      setEmail(r.data);
    } catch (e) { fail(e); } finally { setBusy(''); }
  }

  // ---------- §12.6 survey import — pure, no AI, works offline ----------
  async function runSurvey() {
    setErr(null);
    try {
      const parsed = parseSurvey(surveyText, p.prices);
      const prov = { source: 'import' as const, setBy: p.user, setAt: nowIso(), detail: 'survey-json' };
      await createRecords('inventoryItems', parsed.inventory.map((iv) => ({
        jobId: p.job.id, cat: iv.cat, species: iv.species, qty: iv.qty, cond: iv.cond,
        notes: iv.notes, photoIds: [], provenance: prov, verification: { verified: false },
      })), { snapshot: false });
      await createRecords('takeoffLines', parsed.takeoff.map((t) => ({
        jobId: p.job.id, priceItemId: t.price.itemId, qty: t.qty, label: t.label,
        provenance: prov, verification: { verified: false },
      })), { snapshot: false });
      setSurveyOpen(false); setSurveyText('');
      await p.onChanged();
      const skippedMsg = parsed.skipped.length
        ? ` Skipped ${parsed.skipped.length}: ${parsed.skipped.map((s) => `${s.item} (${s.reason})`).join('; ')}.`
        : '';
      toast(`Survey imported: ${parsed.inventory.length} inventory + ${parsed.takeoff.length} takeoff line(s), all unverified.${skippedMsg}`);
    } catch (e) { setErr(explainStorageError(e) ?? (e as Error).message); }
  }

  // ---------- review queue actions ----------
  async function accept(r: ReviewItem, qtyOverride?: number) {
    const pay = r.payload as LinePayload | InvPayload;
    if (pay.kind === 'line') {
      const price = p.prices.find((x) => x.itemId === pay.item);
      if (!price) { toast(`"${pay.item}" is not in the price book — rejected instead`); await reject(r); return; }
      await createRecord('takeoffLines', {
        jobId: p.job.id, priceItemId: price.itemId, qty: qtyOverride ?? pay.qty, label: pay.label,
        provenance: { source: 'ai', setBy: p.user, setAt: nowIso(), detail: `${r.feature}${pay.basis ? ` · ${pay.basis}` : ''}`, confidence: r.confidence },
        verification: { verified: false },
      }, { snapshot: false });
    } else {
      await createRecord('inventoryItems', {
        jobId: p.job.id, cat: pay.cat, species: pay.species, qty: qtyOverride ?? pay.qty, cond: pay.cond,
        notes: `${pay.notes ?? ''}${r.confidence === 'low' ? ' [low-confidence AI guess — verify species on site]' : ''}`.trim(),
        photoIds: [],
        provenance: { source: 'ai', setBy: p.user, setAt: nowIso(), detail: r.feature, confidence: r.confidence },
        verification: { verified: false },
      }, { snapshot: false });
    }
    await putRecord('reviewQueue', { ...r, status: qtyOverride != null ? 'edited' : 'accepted' });
    await p.onChanged();
  }

  async function reject(r: ReviewItem) {
    await putRecord('reviewQueue', { ...r, status: 'rejected' });
    await p.onChanged();
  }

  async function clearHandled() {
    for (const r of p.review.filter((x) => x.status !== 'pending')) await softDelete('reviewQueue', r.id);
    await p.onChanged();
  }

  return (
    <section class="card" aria-label="AI assistant">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px;">
        <h2>AI assistant</h2>
        {month && <span class={`chip ${month.status === 'ok' ? '' : month.status === 'warn' ? 'chip-warn' : 'chip-err'}`}>
          <b>AI this month</b> ${month.spentUsd.toFixed(2)} / ${month.capUsd.toFixed(0)}
        </span>}
      </div>

      {aiOff && <Banner kind="info">{offReason} The survey import below needs no AI at all.</Banner>}
      {err && <Banner onClose={() => setErr(null)}>{err}</Banner>}

      <div class="field">
        <label>Describe the scope in your own words — a list is fine</label>
        <textarea style="width:100%;min-height:56px;font-size:13.5px;"
          placeholder={'Write it however you like — one per line, bullets, or a sentence:\n- overseed the commons, about 18,000 sq ft\n- replace 40 shrubs\n- remove 2 dead mesquites'}
          value={scope} onInput={(e) => setScope((e.target as HTMLTextAreaElement).value)} aria-label="Scope in plain words" />
        <div style="display:flex;gap:6px;margin-top:6px;flex-wrap:wrap;">
          <button class="btn btn-small btn-primary" disabled={aiOff || busy !== ''} onClick={() => void runScope()}>
            {busy === 'scope' ? 'Matching…' : 'Scope this — match to price book (~$0.01)'}
          </button>
          <button class="btn btn-small btn-ghost" disabled={aiOff || busy !== ''} onClick={() => void runBidMap()}>
            {busy === 'bidmap' ? 'Drafting…' : 'Inventory this — draft lines from site data (~$0.01)'}
          </button>
          {/* A <label> cannot carry the disabled attribute, so when AI is off this
              looked disabled to a person and looked like ordinary low-contrast
              text to axe. aria-disabled says what it is; the opacity is raised to
              the point where the text still passes contrast either way. */}
          <label class="btn btn-small btn-ghost" aria-disabled={aiOff || busy !== ''}
            style={aiOff || busy !== '' ? 'opacity:.62;pointer-events:none;' : 'cursor:pointer;'}>
            {busy === 'photos' ? 'Reading photos…' : 'Photos → inventory (~$0.02/photo)'}
            <input type="file" accept="image/*" multiple style="display:none;" onChange={(e) => void runPhotos(e)} aria-label="Upload site photos" />
          </label>
          <button class="btn btn-small btn-ghost" disabled={busy !== ''} onClick={() => void runSanity()}>Sanity-check this bid (free)</button>
          <button class="btn btn-small btn-ghost" onClick={() => setSurveyOpen(true)}>Import survey JSON…</button>
        </div>
        <p class="small muted" style="margin-top:4px;">
          Lists, bullets and line breaks all work. Photos identify things; they never measure them.
          Every AI draft lands in the review queue below for you to accept, edit, or reject.
        </p>
      </div>

      {skipped.length > 0 && (
        <Banner kind="info" onClose={() => setSkipped([])}>
          <b>Not matched (reported, never silently dropped):</b> {skipped.map((s) => `“${s.text}” — ${s.reason}`).join(' · ')}
        </Banner>
      )}

      {sanity && (
        <div class="card" style="background:rgba(200,167,107,.10);margin-bottom:10px;">
          <h3 style="margin-bottom:6px;">Sanity check — advisory only, nothing blocks</h3>
          {!sanity.enough && <p class="small muted">Fewer than {MIN_HISTORY} issued proposals in history, so price-band comparisons stay quiet rather than flagging on thin data (§12.8). Structural checks still ran.</p>}
          {sanity.flags.length === 0
            ? <p class="small">No flags. {sanity.enough ? 'Within historical bands.' : ''}</p>
            : <ul style="list-style:none;margin-top:4px;">{sanity.flags.map((f) => <li key={f.text} class="small" style="padding:3px 0;">⚑ {f.text}</li>)}</ul>}
          {sanity.summary && <p class="small" style="margin-top:6px;"><span class="srcchip srcchip-ai">AI</span> {sanity.summary}</p>}
        </div>
      )}

      <div class="field" style="margin-bottom:8px;">
        <label>Draft a client email (§5.9 — always a draft; this app never sends anything)</label>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <select value={emailKind} onChange={(e) => setEmailKind((e.target as HTMLSelectElement).value as typeof emailKind)} aria-label="Email type" style="min-height:36px;">
            <option value="bid_follow_up">Bid follow-up</option>
            <option value="schedule_change">Schedule change</option>
            <option value="progress_summary">Progress summary</option>
          </select>
          <button class="btn btn-small btn-ghost" disabled={aiOff || busy !== ''} onClick={() => void runEmail()}>
            {busy === 'email' ? 'Drafting…' : 'Draft email (~$0.03)'}
          </button>
        </div>
        {email && (
          <div style="margin-top:8px;">
            <input style="width:100%;font-weight:600;" value={email.subject} onInput={(e) => setEmail({ ...email, subject: (e.target as HTMLInputElement).value })} aria-label="Email subject" />
            <textarea style="width:100%;min-height:120px;font-size:13.5px;margin-top:6px;" value={email.body} onInput={(e) => setEmail({ ...email, body: (e.target as HTMLTextAreaElement).value })} aria-label="Email body" />
            <button class="btn btn-small btn-ghost" style="margin-top:6px;" onClick={() => { void navigator.clipboard.writeText(`Subject: ${email.subject}\n\n${email.body}`); toast('Draft copied — paste it into your email app and send it yourself'); }}>Copy draft</button>
          </div>
        )}
      </div>

      <div>
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3>Review queue {pending.length > 0 && <span class="chip" style="border-color:var(--paloverde);color:var(--paloverde);">{pending.length} waiting</span>}</h3>
          {p.review.some((x) => x.status !== 'pending') && <button class="btn btn-small btn-ghost" onClick={() => void clearHandled()}>Clear handled</button>}
        </div>
        {pending.length === 0 ? (
          <p class="small muted" style="margin-top:4px;">Empty. AI drafts land here for your accept / edit / reject — item by item, never all-or-nothing.</p>
        ) : (
          <table class="data" style="margin-top:6px;">
            <thead><tr><th>Draft</th><th class="money">Qty</th><th>Conf.</th><th></th></tr></thead>
            <tbody>
              {pending.map((r) => {
                const pay = r.payload as LinePayload | InvPayload;
                const price = pay.kind === 'line' ? p.prices.find((x) => x.itemId === pay.item) : undefined;
                return (
                  <tr key={r.id}>
                    <td>
                      <span class="srcchip srcchip-ai">AI</span>{' '}
                      {pay.kind === 'line'
                        ? <b>{price?.name ?? pay.item}</b>
                        : <b>{pay.cat}{pay.species ? ` — ${pay.species}` : ''}</b>}
                      <div class="muted small">
                        {pay.kind === 'line' ? pay.label : `${pay.cond}${pay.notes ? ` · ${pay.notes}` : ''}`}
                        {pay.kind === 'line' && pay.basis ? ` · basis: ${pay.basis}` : ''}
                        {pay.kind === 'line' && price ? ` · ${fmtCents(price.rateCents)}/${price.unit}` : ''}
                      </div>
                    </td>
                    <td class="money">
                      <input type="number" inputMode="decimal" defaultValue={String(pay.qty)} style="width:84px;text-align:right;min-height:32px;padding:3px 6px;"
                        aria-label={`Quantity for draft ${pay.kind === 'line' ? (price?.name ?? pay.item) : pay.cat}`} id={`rq-${r.id}`} />
                    </td>
                    <td><span class={`chip ${r.confidence === 'low' ? 'chip-warn' : ''}`} style="font-size:11px;">{r.confidence}{r.confidence === 'low' ? ' — flagged' : ''}</span></td>
                    <td style="white-space:nowrap;">
                      <button class="btn btn-small btn-primary" onClick={() => {
                        const el = document.getElementById(`rq-${r.id}`) as HTMLInputElement;
                        const v = parseFloat(el.value);
                        void accept(r, Number.isFinite(v) && v !== pay.qty ? v : undefined);
                      }}>Accept</button>{' '}
                      <button class="btn btn-small btn-ghost" onClick={() => void reject(r)}>Reject</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {surveyOpen && (
        <div class="modal-backdrop" role="dialog" aria-modal="true" aria-label="Import survey">
          <div class="modal">
            <h2 style="margin-bottom:8px;">Import survey JSON</h2>
            <p class="small muted" style="margin-bottom:8px;">Paste the survey block from a chat (the one starting with {'{"app":"landscaping-bid-studio-survey"'}). Unknown items are reported as skipped, never silently dropped. No AI involved; works offline.</p>
            <textarea style="width:100%;min-height:160px;font-family:var(--f-mono);font-size:12px;" value={surveyText}
              onInput={(e) => setSurveyText((e.target as HTMLTextAreaElement).value)} aria-label="Survey JSON" />
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:10px;">
              <button class="btn btn-ghost" onClick={() => setSurveyOpen(false)}>Cancel</button>
              <button class="btn btn-primary" onClick={() => void runSurvey()}>Import survey</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

async function recompress(file: File, maxDim: number): Promise<{ mime: string; b64: string }> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale), h = Math.round(bmp.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d')!.drawImage(bmp, 0, 0, w, h);
  const blob = await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), 'image/jpeg', 0.82));
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return { mime: 'image/jpeg', b64: btoa(bin) };
}

function b64ToBuf(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}
